// src/components/fnct/PlanActions.tsx — SCE Issues
// Le plan d'actions d'une FNC-T (lot F3, piste A + frise, 06/10).
// Maquette : https://claude.ai/artifact/LCfQEHVXcRmKcA6CSDy6ix
//
// Qui · Quoi · Pour quand, trié par échéance (les closes en bas) ; une bascule
// montre la même chose en frise (de l'ouverture à l'échéance, aujourd'hui
// marqué). Les gestes se font dans la ligne : clore, reporter, modifier ; et
// « Ajouter » en tête.
//
// Refonte du 12/10 (https://claude.ai/artifact/M5GHzpFzqJQ3kmh8neSXRr) :
// l'avancement en barre (terminées, en retard, à venir), « Reporter » visible
// à côté de « Clore », le menu ⋯ réduit à « Modifier l'action » (gestionnaires
// seulement), la frise qui montre aussi le retard RATTRAPÉ (action close après
// son échéance) avec une légende, et le commentaire de clôture en français
// (il ne part pas dans le rapport ; Guillaume, 12/10). Le responsable d'une action clôt ou reporte la sienne ;
// les gestionnaires (coordinateurs, AQ / CQ, admin) agissent sur toutes.
// Règles : utils/actionsFnct ; écriture : api/fnct.api.

import { useEffect, useId, useState, type CSSProperties, type JSX, type ReactNode } from 'react'
import { AlertCircle, CalendarClock, CheckCircle2, ChevronDown, ChevronUp, Clock, Plus } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { usePreferenceLocale } from '@/hooks/usePreferenceLocale'
import { useEstBureau } from '@/hooks/useMediaQuery'
import { useConfirmerEcriture } from '@/features/vue-role/contexte'
import { ajouterAction, cloreAction, confierAction, modifierAction, reporterAction } from '@/api/fnct.api'
import { STYLE_AIDE, STYLE_CHAMP, STYLE_ETIQUETTE } from '@/components/issues/FenetreQualite'
import {
  axeFrise,
  geometrieFrise,
  erreurConfier,
  nomCourt,
  peutAgirSurAction,
  pourQuand,
  resumeActionFrise,
  textesPointsFrise,
  type TextePoint,
  trierPlan,
  type Destinataire,
  type SaisieAction,
} from '@/utils/actionsFnct'
import { ajouterJours } from '@/utils/escaladeFnct'
import { compterActions, jourLisible, memePersonne } from '@/utils/fnct'
import { initialesDe } from '@/utils/noms'
import { lesPlusFrequents } from '@/utils/annuaire'
import { ChoixPersonne } from '@/components/personnes/ChoixPersonne'
import { IconeRapport } from '@/components/fnct/IconeRapport'
import { ChoixDate } from '@/components/ui/ChoixDate'
import { PastillePersonne } from '@/components/personnes/PastillePersonne'
import { MentionAttenteBE } from '@/components/fnct/EtatsFnct'
import { Infobulle } from '@/components/ui/Infobulle'
import { GestesAction } from '@/components/fnct/GestesAction'
import { avertissementDelaiBE } from '@/utils/attenteBE'
import { beAttendu } from '@/utils/attenteBE'
import { AIDES_CATEGORIE_ACTION } from '@/config/fnct.config'
import {
  CATEGORIES_ACTION,
  LIBELLES_CATEGORIE_ACTION,
  type ActionFnct,
  type CategorieAction,
  type Fnct,
  type Jour,
} from '@/types/fnct.types'

type Geste = 'clore' | 'reporter' | 'confier' | 'modifier'

export interface PlanActionsProps {
  numero: string
  actions: readonly ActionFnct[]
  /** Toutes les actions connues : services et noms proposés. */
  toutes: readonly ActionFnct[]
  aujourdhui: Jour
  /** Coordinateurs, AQ / CQ, administrateur. */
  gestionnaire: boolean
  /** « Prénom Nom » de l'utilisateur réel (auteur dans l'historique). */
  nomUtilisateur: string
  /**
   * Qui l'on regarde, pour les droits (« son action ? ») : la personne affichée en
   * bascule de rôle (30/10 : les gestes se montrent partout de la même façon, avec
   * confirmation) ; l'utilisateur réel sinon.
   */
  nomDroits?: string
  /** Après une écriture : relire les listes. */
  onModifie: () => void
  /** Actions closes dépliées d'office (FNC-T prête à clore : on vérifie avant de clore). */
  deplierTerminees?: boolean
  /** Arrivée par Clore / Reporter de « Mes FNC-T » (22/10) : ce geste s'ouvre sur cette action, le tableau affiché. */
  aOuvrir?: { id: string; geste: 'clore' | 'reporter' }
  /** La FNC-T : dit quelles actions attendent le BE (P&ID / PPL, EDE, HDS — utils/attenteBE). */
  fnct?: Pick<Fnct, 'categorie' | 'ecarts'>
}

const RACCOURCIS: readonly { libelle: string; jours: number }[] = [
  { libelle: '1 sem.', jours: 7 },
  { libelle: '2 sem.', jours: 14 },
  { libelle: '4 sem.', jours: 28 },
  { libelle: '3 mois', jours: 91 },
]

export function PlanActions({ numero, actions, toutes, aujourdhui, gestionnaire, nomUtilisateur, nomDroits, onModifie, deplierTerminees = false, aOuvrir, fnct }: PlanActionsProps): JSX.Element {
  const estBureau = useEstBureau()
  const confirmerEcriture = useConfirmerEcriture()
  const [friseRetenue, setEnFrise] = usePreferenceLocale('sce-issues.fnct.plan-en-frise', false)
  // Arrivée sur un geste : le tableau, même si la frise est retenue (un clic sur la bascule la rend).
  const [tableauForce, setTableauForce] = useState(!!aOuvrir)
  const enFrise = friseRetenue && !tableauForce
  const [ajout, setAjout] = useState(false)
  const [ouverte, setOuverte] = useState<{ id: string; geste: Geste } | null>(aOuvrir ?? null)
  useEffect(() => {
    if (aOuvrir) document.getElementById(`action-${aOuvrir.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [aOuvrir])
  const [envoi, setEnvoi] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [termineesChoix, setTermineesChoix] = useState<boolean | null>(null)
  const termineesOuvertes = termineesChoix ?? deplierTerminees

  const plan = trierPlan(actions)
  const c = compterActions(actions, aujourdhui)
  const habitues = lesPlusFrequents(toutes.map((a) => a.responsable))
  const serviceDe = (nom: string): string => [...toutes].reverse().find((a) => memePersonne(a.responsable, nom))?.service ?? ''

  async function ecrire(libelle: string, faire: () => Promise<unknown>): Promise<void> {
    if (!confirmerEcriture(libelle)) return
    setEnvoi(true)
    setErreur(null)
    try {
      await faire()
      setAjout(false)
      setOuverte(null)
      onModifie()
    } catch (err) {
      setErreur(err instanceof Error ? err.message : String(err))
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <section id="plan" aria-labelledby="titre-plan" className="flex flex-col gap-3" style={STYLE_SECTION_FICHE}>
      <div className="flex items-center justify-between gap-3" style={{ flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div className="flex items-center gap-2">
            <h2 id="titre-plan" style={STYLE_TITRE}>
              Le plan d’actions
            </h2>
            <IconeRapport detail="Chaque action : description, responsable et date de clôture." />
          </div>
          <div style={{ fontSize: 14, color: 'var(--color-gray-80)', marginTop: 2 }}>
            {c.total === 0
              ? 'Aucune action pour l’instant'
              : `${c.terminees} sur ${c.total} terminée${c.terminees > 1 ? 's' : ''}`}
            {c.enRetard > 0 && <span style={{ color: 'var(--color-traffic-red)', fontWeight: 600 }}> · {c.enRetard} en retard</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {actions.length > 0 && (
            <div role="radiogroup" aria-label="Affichage du plan" className="flex" style={{ border: '1px solid var(--color-border)', borderRadius: 18, overflow: 'hidden' }}>
              {[false, true].map((frise) => (
                <button
                  key={String(frise)}
                  type="button"
                  role="radio"
                  aria-checked={enFrise === frise}
                  onClick={() => {
                    setEnFrise(frise)
                    setTableauForce(false)
                  }}
                  style={{
                    height: 34,
                    padding: '0 12px',
                    border: 'none',
                    background: enFrise === frise ? 'var(--color-black)' : 'var(--color-white)',
                    color: enFrise === frise ? 'var(--color-white)' : 'var(--color-black)',
                    fontSize: 13,
                    fontWeight: enFrise === frise ? 600 : 400,
                    cursor: 'pointer',
                  }}
                >
                  {frise ? 'Frise' : 'Liste'}
                </button>
              ))}
            </div>
          )}
          {gestionnaire && !ajout && (
            <Button variant="secondary" size="sm" onClick={() => setAjout(true)}>
              + Ajouter
            </Button>
          )}
        </div>
      </div>

      {c.total > 0 && <Avancement terminees={c.terminees} enRetard={c.enRetard} total={c.total} />}

      {erreur && <p style={{ ...STYLE_AIDE, color: 'var(--color-error)' }}>{erreur}</p>}

      {ajout && (
        <FormAction
          titre="Ajouter une action"
          depart={{ categorie: 'CO', description: '', responsable: '', service: '', echeance: ajouterJours(aujourdhui, 14) }}
          serviceDe={serviceDe}
          habitues={habitues}
          aujourdhui={aujourdhui}
          envoi={envoi}
          libelle="Ajouter"
          onAnnuler={() => setAjout(false)}
          onValider={(s) => void ecrire('Ajouter une action', () => ajouterAction(numero, s, aujourdhui, nomUtilisateur))}
        />
      )}

      {actions.length === 0 && !ajout && (
        <div className="text-center py-6" style={{ borderRadius: 'var(--radius-lg)', backgroundColor: 'var(--color-white)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-copy2)' }}>
          Aucune action.
        </div>
      )}

      {actions.length > 0 && enFrise && <FriseActions actions={plan} aujourdhui={aujourdhui} />}

      {actions.length > 0 && !enFrise && (
        <div style={{ backgroundColor: 'var(--color-white)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          {estBureau && (c.terminees < c.total || termineesOuvertes) && (
            <div style={{ ...GRILLE, padding: '8px 16px', backgroundColor: 'var(--color-gray-5)', fontSize: 11, fontWeight: 600, color: 'var(--color-gray-70)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              <span>Qui</span>
              <span>Quoi</span>
              <span>Pour quand</span>
              <span />
            </div>
          )}
          {plan.filter((a) => !a.cloture || termineesOuvertes).map((a) => {
            const agir = peutAgirSurAction(nomDroits ?? nomUtilisateur, a, gestionnaire)
            const geste = ouverte?.id === a.id ? ouverte.geste : null
            return (
              <div key={a.id} id={`action-${a.id}`} style={{ borderTop: '1px solid var(--color-gray-10)' }}>
                <LigneAction
                  action={a}
                  attendBE={!!fnct && !!beAttendu(a, fnct, actions)}
                  aujourdhui={aujourdhui}
                  bureau={estBureau}
                  boutons={
                    !a.cloture &&
                    agir && (
                      // Les ronds de « Mes FNC-T » (option B, 24/10) ; Modifier en dernier pour qui gère les actions.
                      <GestesAction
                        ouvert={geste === 'modifier' ? null : geste}
                        onGeste={(g) => setOuverte(geste === g ? null : { id: a.id, geste: g })}
                        onModifier={gestionnaire ? () => setOuverte(geste === 'modifier' ? null : { id: a.id, geste: 'modifier' }) : undefined}
                        modifierOuvert={geste === 'modifier'}
                      />
                    )
                  }
                />
                {geste === 'clore' && a.itemId && (
                  <FormCloture
                    aujourdhui={aujourdhui}
                    min={a.ouverture}
                    envoi={envoi}
                    onAnnuler={() => setOuverte(null)}
                    onValider={(date, commentaire) => void ecrire('Clore une action', () => cloreAction(a.itemId ?? '', date, commentaire, aujourdhui, nomUtilisateur))}
                  />
                )}
                {geste === 'reporter' && a.itemId && (
                  <FormReport
                    depart={ajouterJours(a.echeanceReportee ?? a.echeance ?? aujourdhui, 14)}
                    min={(a.echeanceReportee ?? a.echeance) ? ajouterJours(a.echeanceReportee ?? a.echeance ?? aujourdhui, 1) : null}
                    envoi={envoi}
                    onAnnuler={() => setOuverte(null)}
                    onValider={(date, motif) => void ecrire('Reporter une action', () => reporterAction(a.itemId ?? '', date, motif, aujourdhui, nomUtilisateur))}
                  />
                )}
                {geste === 'confier' && a.itemId && (
                  <FormConfier
                    action={a}
                    habitues={habitues}
                    serviceDe={serviceDe}
                    envoi={envoi}
                    onAnnuler={() => setOuverte(null)}
                    onValider={(vers, motif) =>
                      void ecrire('Confier une action', () => confierAction(a.itemId ?? '', a.responsable, vers, motif, aujourdhui, nomUtilisateur))
                    }
                  />
                )}
                {geste === 'modifier' && a.itemId && (
                  <div style={{ padding: '0 16px 12px' }}>
                    <FormAction
                      titre="Modifier l'action"
                      ouverteLe={a.ouverture}
                      // Reportée : on modifie l'échéance EN VIGUEUR (la reportée), celle que montrent les listes (30/10).
                      echeanceInitiale={a.echeanceReportee ? a.echeance : null}
                      depart={{
                        categorie: a.categorie || 'CO',
                        description: a.description,
                        responsable: a.responsable,
                        responsableUPN: a.responsableUPN,
                        service: a.service,
                        echeance: a.echeanceReportee ?? a.echeance,
                      }}
                      serviceDe={serviceDe}
                      habitues={habitues}
                      aujourdhui={aujourdhui}
                      envoi={envoi}
                      libelle="Enregistrer"
                      responsableInitial={a.responsable}
                      onAnnuler={() => setOuverte(null)}
                      onValider={(s, motif) => void ecrire('Modifier une action', () => modifierAction(a.itemId ?? '', s, nomUtilisateur, motif, aujourdhui))}
                    />
                  </div>
                )}
              </div>
            )
          })}
          {c.terminees > 0 && (
            <button
              type="button"
              onClick={() => setTermineesChoix(!termineesOuvertes)}
              aria-expanded={termineesOuvertes}
              className="flex items-center justify-between w-full"
              style={{ gap: 12, padding: '12px 16px', border: 'none', borderTop: '1px solid var(--color-gray-10)', background: 'var(--color-white)', textAlign: 'left', cursor: 'pointer' }}
            >
              <span className="inline-flex items-center gap-2" style={{ fontSize: 14, fontWeight: 500 }}>
                <CheckCircle2 size={16} color="var(--color-traffic-green)" aria-hidden="true" />
                {c.terminees === c.total ? `Toutes les actions sont closes · ${c.total}` : `${c.terminees} action${c.terminees > 1 ? 's closes' : ' close'}`}
              </span>
              <span className="inline-flex items-center gap-1" style={{ fontSize: 13, color: 'var(--color-gray-80)' }}>
                {termineesOuvertes ? 'Masquer' : 'Afficher'}
                {termineesOuvertes ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
              </span>
            </button>
          )}
        </div>
      )}

      {gestionnaire && !ajout && (
        <button
          type="button"
          onClick={() => setAjout(true)}
          className="flex items-center justify-center gap-2 w-full"
          style={{ height: 46, borderRadius: 'var(--radius-lg)', border: '1px dashed var(--color-gray-50)', background: 'var(--color-white)', color: 'var(--color-gray-90)', fontSize: 14, cursor: 'pointer' }}
        >
          <Plus size={16} aria-hidden="true" />
          Ajouter une action : correction, analyse des causes, corrective, préventive
        </button>
      )}
    </section>
  )
}

// ─── Le tableau ───

const GRILLE: CSSProperties = {
  display: 'grid',
  // Les gestes : Clore, Reporter, Confier, Modifier (4 ronds de 52 px, 30/10).
  gridTemplateColumns: '150px minmax(0, 1fr) 140px 216px',
  gap: 12,
  alignItems: 'center',
}

const STYLE_TITRE: CSSProperties = {
  margin: 0,
  fontSize: 20,
  lineHeight: '26px',
  fontWeight: 500,
}

/**
 * Carte blanche d'une section de la fiche FNC-T (refonte du 12/10). Les arrondis de la
 * fiche (02/11), ceux de la fiche problème et du cadre en surimpression : cartes 12
 * (--radius-xl) ; tout rectangle dans une carte — encadré, tableau, frise, formulaire,
 * ligne ou bouton, champ — 8 (--radius-lg) ; étiquettes (CO, P&ID…) 6 (--radius-md) ;
 * le rond reste rond (pastilles, boutons ronds, pilules à mi-hauteur).
 */
export const STYLE_SECTION_FICHE: CSSProperties = {
  backgroundColor: 'var(--color-white)',
  borderRadius: 'var(--radius-xl)',
  padding: '20px 20px',
}

const HACHURES = 'repeating-linear-gradient(135deg, var(--color-traffic-red) 0 4px, var(--color-traffic-red-pale) 4px 8px)'

/** Barre d'avancement : terminées (vert), en retard (rouge), à venir (gris). */
function Avancement({ terminees, enRetard, total }: { terminees: number; enRetard: number; total: number }): JSX.Element {
  const aVenir = Math.max(0, total - terminees - enRetard)
  const part = (n: number): string => `${(n / total) * 100}%`
  const legende = [
    { n: terminees, texte: terminees > 1 ? 'terminées' : 'terminée', couleur: 'var(--color-traffic-green)' },
    { n: enRetard, texte: 'en retard', couleur: 'var(--color-traffic-red)' },
    { n: aVenir, texte: 'à venir', couleur: 'var(--color-gray-80)' },
  ].filter((l) => l.n > 0)
  return (
    <div>
      <div role="img" aria-label={legende.map((l) => `${l.n} ${l.texte}`).join(', ')} className="flex" style={{ height: 8, borderRadius: 4, overflow: 'hidden', backgroundColor: 'var(--color-gray-10)' }}>
        {legende.map((l) => (
          <span key={l.texte} style={{ width: part(l.n), backgroundColor: l.couleur }} />
        ))}
      </div>
      <div className="flex" style={{ flexWrap: 'wrap', gap: '4px 18px', marginTop: 8, fontSize: 13, color: 'var(--color-gray-80)' }}>
        {legende.map((l) => (
          <span key={l.texte} className="inline-flex items-center gap-1.5">
            <span style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: l.couleur }} />
            {l.n} {l.texte}
          </span>
        ))}
      </div>
    </div>
  )
}

function Pastille({ nom, eteinte }: { nom: string; eteinte: boolean }): JSX.Element {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 28,
        height: 28,
        flexShrink: 0,
        borderRadius: 14,
        backgroundColor: eteinte ? 'var(--color-gray-20)' : 'var(--color-black)',
        color: eteinte ? 'var(--color-black)' : 'var(--color-white)',
        fontSize: 11,
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {initialesDe(nom)}
    </span>
  )
}

function Categorie({ c, eteinte }: { c: CategorieAction | ''; eteinte: boolean }): JSX.Element | null {
  if (!c) return null
  return (
    <span
      title={LIBELLES_CATEGORIE_ACTION[c].fr}
      style={{
        fontSize: 11,
        fontWeight: 700,
        border: `1px solid ${eteinte ? 'var(--color-gray-50)' : 'var(--color-black)'}`,
        borderRadius: 'var(--radius-md)',
        padding: '0 4px',
        marginRight: 6,
      }}
    >
      {c}
    </span>
  )
}

function IconeEtat({ a, aujourdhui }: { a: ActionFnct; aujourdhui: Jour }): JSX.Element {
  const q = pourQuand(a, aujourdhui)
  if (a.cloture) return <CheckCircle2 size={14} color="var(--color-traffic-green)" aria-hidden="true" />
  if (q.retard) return <AlertCircle size={14} color="var(--color-traffic-red)" aria-hidden="true" />
  if (a.echeanceReportee) return <CalendarClock size={14} aria-hidden="true" />
  return <Clock size={14} aria-hidden="true" />
}

function LigneAction({ action: a, attendBE, aujourdhui, bureau, boutons }: { action: ActionFnct; attendBE: boolean; aujourdhui: Jour; bureau: boolean; boutons: ReactNode }): JSX.Element {
  const q = pourQuand(a, aujourdhui)
  const eteinte = Boolean(a.cloture)
  const quand = (
    <span className="flex flex-col" style={{ fontSize: 14 }}>
      <span
        className="inline-flex items-center gap-1"
        style={{ color: q.retard ? 'var(--color-traffic-red)' : eteinte ? 'var(--color-gray-70)' : 'var(--color-text-primary)', fontWeight: q.retard ? 600 : 400 }}
      >
        <IconeEtat a={a} aujourdhui={aujourdhui} />
        {q.texte}
      </span>
      {q.precision && <span style={{ fontSize: 12, color: 'var(--color-gray-70)' }}>{q.precision}</span>}
      {attendBE && <MentionAttenteBE />}
    </span>
  )
  const qui = (
    <span className="flex items-center gap-2" style={{ fontSize: 14, fontWeight: 500, color: eteinte ? 'var(--color-gray-70)' : undefined, minWidth: 0 }}>
      {eteinte || !a.responsableUPN ? (
        <Pastille nom={a.responsable} eteinte={eteinte} />
      ) : (
        <PastillePersonne nom={a.responsable} upn={a.responsableUPN} taille={28} />
      )}
      <span title={a.responsable} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {nomCourt(a.responsable) || '—'}
      </span>
    </span>
  )
  // Le motif des reports et le commentaire de clôture se LISENT (maquette du 12/10) : au survol
  // seulement, on ne les voyait jamais.
  const quoi = (
    <span className="flex flex-col" style={{ minWidth: 0 }}>
      <span style={{ fontSize: 14, color: eteinte ? 'var(--color-gray-70)' : undefined, overflowWrap: 'anywhere' }}>
        <Categorie c={a.categorie} eteinte={eteinte} />
        {a.description}
      </span>
      {a.commentaire.trim() && (
        <span
          style={{ marginTop: 4, fontSize: 12, lineHeight: '17px', color: eteinte ? 'var(--color-gray-70)' : 'var(--color-gray-90)', fontStyle: 'italic', whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}
        >
          {a.commentaire.trim()}
        </span>
      )}
    </span>
  )
  if (bureau) {
    return (
      <div style={{ ...GRILLE, padding: '12px 16px' }}>
        {qui}
        {quoi}
        {quand}
        <span className="flex" style={{ justifyContent: 'flex-end' }}>
          {boutons}
        </span>
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-2" style={{ padding: '12px 14px' }}>
      {quoi}
      <div className="flex items-center justify-between gap-2" style={{ flexWrap: 'wrap' }}>
        {qui}
        {quand}
      </div>
      {boutons}
    </div>
  )
}

// ─── La frise ───

/** Zone survolable autour d'un point : plus large que le point, pour la souris. */
const CIBLE_POINT = 20

/** Un point de la frise et son infobulle : ce qui s'est passé ce jour-là. */
function PointFrise({ position, texte, children }: { position: number; texte: TextePoint; children: ReactNode }): JSX.Element {
  return (
    <Infobulle
      titre={texte.titre}
      lignes={texte.lignes}
      largeur={340}
      style={{
        position: 'absolute',
        left: `${position}%`,
        top: 0,
        width: CIBLE_POINT,
        height: 22,
        marginLeft: -CIBLE_POINT / 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'default',
      }}
    >
      {children}
    </Infobulle>
  )
}

/**
 * La frise (maquette du 12/10), revue le 28/10 avec Guillaume : quelques repères
 * pour situer le temps (années, trimestres, mois, lundis ou jours selon la durée),
 * pas de week-ends, une infobulle sur chaque POINT (ouverture, échéance initiale,
 * échéance ou clôture). Aujourd'hui n'apparaît que tant qu'une action est ouverte :
 * une FNC-T close se lit de sa première ouverture à sa dernière clôture. Sa ligne
 * passe sous les barres et son libellé efface les repères voisins : il ne cache rien.
 */
function FriseActions({ actions, aujourdhui }: { actions: readonly ActionFnct[]; aujourdhui: Jour }): JSX.Element {
  const f = geometrieFrise(actions, aujourdhui)
  const axe = axeFrise(f.debut, f.fin, f.aujourdhui === null ? null : aujourdhui)
  const couleur: Record<string, string> = {
    close: 'var(--color-traffic-green)',
    retard: 'var(--color-traffic-red)',
    reportee: 'var(--color-gray-50)',
    ouverte: 'var(--color-gray-20)',
  }
  const grille = 'minmax(140px, 240px) minmax(0, 1fr)'
  return (
    <div style={{ backgroundColor: 'var(--color-white)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: '12px 16px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: grille, gap: 12, fontSize: 11, color: 'var(--color-gray-70)', paddingBottom: 6, borderBottom: '1px solid var(--color-gray-10)' }}>
        <span />
        <span style={{ position: 'relative', height: 14 }}>
          {axe.reperes.map((m) => (
            <span key={m.jour} style={{ position: 'absolute', left: `${m.position}%`, paddingLeft: 4, borderLeft: '1px solid var(--color-gray-20)', whiteSpace: 'nowrap' }}>
              {m.libelle}
            </span>
          ))}
          {axe.aujourdhui !== null && (
            <span
              style={{
                position: 'absolute',
                left: `${axe.aujourdhui}%`,
                transform: axe.aujourdhui > 85 ? 'translateX(-100%)' : axe.aujourdhui < 15 ? 'none' : 'translateX(-50%)',
                color: 'var(--color-black)',
                fontWeight: 700,
                whiteSpace: 'nowrap',
              }}
            >
              aujourd’hui
            </span>
          )}
        </span>
      </div>
      {actions.map((a, i) => {
        const l = f.lignes[i]
        if (!l) return null
        const q = pourQuand(a, aujourdhui)
        const textes = textesPointsFrise(a, aujourdhui)
        return (
          <div key={a.id} style={{ display: 'grid', gridTemplateColumns: grille, gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--color-gray-10)' }}>
            <div style={{ minWidth: 0, color: a.cloture ? 'var(--color-gray-70)' : undefined }}>
              <div title={a.description} style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {a.description}
              </div>
              <div style={{ fontSize: 12, color: q.retard ? 'var(--color-traffic-red)' : 'var(--color-gray-70)' }}>
                {nomCourt(a.responsable)} · {a.categorie || '—'} · {q.texte}
              </div>
            </div>
            <div style={{ position: 'relative', height: 22 }}>
              <span className="sr-only">{resumeActionFrise(a, aujourdhui)}</span>
              {/* Aujourd'hui d'abord : tout le reste se dessine par-dessus. */}
              {f.aujourdhui !== null && (
                <div aria-hidden="true" style={{ position: 'absolute', left: `${f.aujourdhui}%`, top: -10, bottom: -10, width: 1, backgroundColor: 'var(--color-gray-50)' }} />
              )}
              <div aria-hidden="true" style={{ position: 'absolute', left: `${l.debut}%`, width: `${Math.max(0.5, l.fin - l.debut)}%`, top: 9, height: 4, borderRadius: 2, backgroundColor: couleur[l.etat] }} />
              {/* Ancré sur son bout et jamais plus court que 16 px : un retard de deux jours sur une frise d'un an restait caché sous le point. */}
              {l.retard && (
                <div
                  aria-hidden="true"
                  style={{ position: 'absolute', right: `${100 - l.retard.fin}%`, width: `max(${l.retard.fin - l.retard.debut}%, 16px)`, top: 8, height: 6, background: HACHURES }}
                />
              )}
              <div aria-hidden="true">
                {textes.ouverture && (
                  <PointFrise position={l.debut} texte={textes.ouverture}>
                    <span style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: 'var(--color-gray-70)' }} />
                  </PointFrise>
                )}
                {l.initiale !== undefined && textes.initiale && (
                  <PointFrise position={l.initiale} texte={textes.initiale}>
                    <span style={{ width: 12, height: 12, borderRadius: 6, border: '2px solid var(--color-gray-50)', boxSizing: 'border-box', backgroundColor: 'var(--color-white)' }} />
                  </PointFrise>
                )}
                {l.echeance !== undefined && textes.echeance && (
                  <PointFrise position={l.echeance} texte={textes.echeance}>
                    <span style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: 'var(--color-black)' }} />
                  </PointFrise>
                )}
                <PointFrise position={l.fin} texte={textes.fin}>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 7,
                      backgroundColor: l.etat === 'close' ? 'var(--color-traffic-green)' : l.etat === 'retard' ? 'var(--color-traffic-red)' : 'var(--color-black)',
                      // Close en retard : le vert dit « close », l'anneau rouge dit « en retard ».
                      boxShadow: l.closeEnRetard ? '0 0 0 2px var(--color-white), 0 0 0 4px var(--color-traffic-red)' : undefined,
                    }}
                  />
                </PointFrise>
              </div>
            </div>
          </div>
        )
      })}
      <div className="flex" style={{ flexWrap: 'wrap', gap: '6px 18px', marginTop: 10, fontSize: 12, color: 'var(--color-gray-80)' }}>
        <span className="inline-flex items-center gap-1.5">
          <span style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: 'var(--color-gray-70)' }} />
          ouverture
        </span>
        <span className="inline-flex items-center gap-1.5" title="Fixée à la création de l’action. N’apparaît que si l’action a été reportée.">
          <span style={{ width: 12, height: 12, borderRadius: 6, border: '2px solid var(--color-gray-50)', boxSizing: 'border-box' }} />
          échéance initiale, si reportée
        </span>
        <span className="inline-flex items-center gap-1.5" title="La date après le dernier report : c’est elle qui compte pour le retard.">
          <span style={{ width: 12, height: 12, borderRadius: 6, backgroundColor: 'var(--color-black)' }} />
          échéance en vigueur
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span style={{ width: 12, height: 12, borderRadius: 6, backgroundColor: 'var(--color-traffic-green)' }} />
          clôture
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span style={{ width: 12, height: 12, borderRadius: 6, backgroundColor: 'var(--color-traffic-green)', boxShadow: '0 0 0 2px var(--color-white), 0 0 0 4px var(--color-traffic-red)', margin: '0 3px' }} />
          clôture en retard
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span style={{ width: 18, height: 6, background: HACHURES }} />
          retard, en cours ou rattrapé
        </span>
      </div>
      <p style={{ ...STYLE_AIDE, marginTop: 8 }}>Survolez un point pour le détail. Clore, reporter : depuis le tableau.</p>
    </div>
  )
}

// ─── Les formulaires ───

function Cadre({ titre, children }: { titre: string; children: ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col gap-3" style={{ backgroundColor: 'var(--color-gray-5)', borderRadius: 'var(--radius-lg)', padding: 12, margin: '0 16px 12px' }}>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{titre}</span>
      {children}
    </div>
  )
}

function Boutons({ libelle, envoi, onAnnuler, onValider, desactive }: { libelle: string; envoi: boolean; onAnnuler: () => void; onValider: () => void; desactive?: boolean }): JSX.Element {
  return (
    <div className="flex justify-end gap-2">
      <Button variant="ghost" size="sm" onClick={onAnnuler} disabled={envoi}>
        Annuler
      </Button>
      <Button variant="primary" size="sm" onClick={onValider} disabled={envoi || desactive}>
        {envoi ? '…' : libelle}
      </Button>
    </div>
  )
}

/** Clore une action : date (aujourd’hui d’office) et commentaire facultatif. Aussi dans « Mes FNC-T » et l’accueil (GesteActionEnPlace). */
export function FormCloture({
  aujourdhui,
  min,
  envoi,
  onAnnuler,
  onValider,
}: {
  aujourdhui: Jour
  /** L'ouverture de l'action : on ne la clôt pas avant (30/10 — refusé jusque-là seulement après le clic). */
  min?: Jour | null
  envoi: boolean
  onAnnuler: () => void
  onValider: (date: Jour, commentaire: string) => void
}): JSX.Element {
  const [date, setDate] = useState<Jour>(aujourdhui)
  const [commentaire, setCommentaire] = useState('')
  return (
    <Cadre titre="Clore l'action">
      <label className="flex items-center gap-3" style={{ fontSize: 13 }}>
        <span style={{ color: 'var(--color-gray-70)', width: 120 }}>Close le</span>
        <ChoixDate libelle="Close le" valeur={date} min={min && min <= aujourdhui ? min : null} max={aujourdhui} onChange={(j) => j && setDate(j)} style={{ maxWidth: 240 }} />
      </label>
      <label className="flex flex-col">
        <span style={STYLE_ETIQUETTE}>Commentaire (facultatif)</span>
        <input value={commentaire} onChange={(e) => setCommentaire(e.target.value)} placeholder="ex. Pièces livrées et montées" style={STYLE_CHAMP} />
      </label>
      <Boutons libelle="Clore l'action" envoi={envoi} onAnnuler={onAnnuler} onValider={() => onValider(date, commentaire)} desactive={!date} />
    </Cadre>
  )
}

/** Reporter l’échéance : nouvelle date et motif obligatoire, en anglais. Aussi dans « Mes FNC-T » et l’accueil. */
export function FormReport({
  depart,
  min,
  envoi,
  onAnnuler,
  onValider,
}: {
  depart: Jour
  /** Le lendemain de l'échéance initiale : un report la suit (30/10 — refusé jusque-là seulement après le clic). */
  min?: Jour | null
  envoi: boolean
  onAnnuler: () => void
  onValider: (date: Jour, motif: string) => void
}): JSX.Element {
  const [date, setDate] = useState<Jour>(depart)
  const [motif, setMotif] = useState('')
  return (
    <Cadre titre="Reporter l'échéance">
      <label className="flex items-center gap-3" style={{ fontSize: 13 }}>
        <span style={{ color: 'var(--color-gray-70)', width: 120 }}>Nouvelle échéance</span>
        <ChoixDate
          libelle="Nouvelle échéance"
          valeur={date}
          min={min}
          onChange={(j) => j && setDate(j)}
          raccourcis={[
            { libelle: '+1 sem.', jour: ajouterJours(depart, 7) },
            { libelle: '+2 sem.', jour: ajouterJours(depart, 14) },
            { libelle: '+4 sem.', jour: ajouterJours(depart, 28) },
          ]}
          style={{ maxWidth: 240 }}
        />
      </label>
      <label className="flex flex-col">
        <span style={STYLE_ETIQUETTE}>Pourquoi ? (obligatoire, en anglais)</span>
        <input value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="ex. Valve delivered late by the supplier" style={STYLE_CHAMP} />
      </label>
      <Boutons libelle="Reporter" envoi={envoi} onAnnuler={onAnnuler} onValider={() => onValider(date, motif)} desactive={!date || !motif.trim()} />
    </Cadre>
  )
}

/**
 * Confier l'action à quelqu'un d'autre (30/10, Guillaume : « commentaire
 * obligatoire ») : la personne, choisie dans l'annuaire, et pourquoi. Aussi dans
 * « Mes FNC-T », l'accueil et le volet de la liste (GesteActionEnPlace).
 */
export function FormConfier({
  action,
  habitues = [],
  serviceDe,
  envoi,
  onAnnuler,
  onValider,
}: {
  action: Pick<ActionFnct, 'cloture' | 'responsable' | 'responsableUPN'>
  /** Proposés champ vide : les responsables d'actions les plus fréquents. */
  habitues?: readonly string[]
  /** Le service d'une personne d'après ses autres actions, quand l'annuaire ne le dit pas. */
  serviceDe?: (nom: string) => string
  envoi: boolean
  onAnnuler: () => void
  onValider: (vers: Destinataire, motif: string) => void
}): JSX.Element {
  const idForm = useId()
  const [vers, setVers] = useState<Destinataire | null>(null)
  const [motif, setMotif] = useState('')
  const erreur = erreurConfier(action, vers, motif)
  // Dite tout de suite : la même personne. Le reste (personne, motif) se lit sur le bouton grisé.
  const memePersonneChoisie = !!vers && erreur?.startsWith('L’action est déjà')
  return (
    <Cadre titre="Confier l'action à quelqu'un d'autre">
      <div className="flex flex-col" style={{ gap: 4 }}>
        <label htmlFor={`${idForm}-vers`} style={STYLE_ETIQUETTE}>
          À qui ?{action.responsable.trim() ? ` (aujourd’hui : ${action.responsable.trim()})` : ''}
        </label>
        <ChoixPersonne
          id={`${idForm}-vers`}
          libelle="À qui confier l’action"
          valeur={{ nom: vers?.nom ?? '', upn: vers?.upn }}
          habitues={habitues.filter((n) => !memePersonne(n, action.responsable))}
          titreHabitues="Souvent responsables d’actions"
          autoFocus
          onChoisir={(p) => setVers(p ? { nom: p.nom, upn: p.upn, service: p.service || serviceDe?.(p.nom) || '' } : null)}
        />
        {memePersonneChoisie && <span style={{ ...STYLE_AIDE, color: 'var(--color-error)' }}>{erreur}</span>}
      </div>
      <label className="flex flex-col">
        <span style={STYLE_ETIQUETTE}>Pourquoi ? (obligatoire, en anglais)</span>
        <input value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="ex. Away until December, Mathieu takes over" style={STYLE_CHAMP} />
      </label>
      <Boutons libelle="Confier" envoi={envoi} onAnnuler={onAnnuler} onValider={() => vers && onValider(vers, motif)} desactive={!!erreur} />
    </Cadre>
  )
}

function FormAction({
  titre,
  depart,
  serviceDe,
  habitues,
  aujourdhui,
  envoi,
  libelle,
  ouverteLe,
  echeanceInitiale,
  responsableInitial,
  onAnnuler,
  onValider,
}: {
  titre: string
  depart: SaisieAction
  /** Depuis quand courent les 10 jours ouvrés d'une action du BE : l'ouverture de l'action (aujourd'hui pour une nouvelle). */
  ouverteLe?: Jour | null
  /** Action déjà reportée : son échéance initiale. La date du formulaire est alors la reportée, qui doit la suivre. */
  echeanceInitiale?: Jour | null
  /** « Modifier » : le responsable actuel. S'il change, le motif est demandé (30/10, comme « Confier »). */
  responsableInitial?: string
  serviceDe: (nom: string) => string
  /** Responsables les plus fréquents, proposés champ vide. */
  habitues: readonly string[]
  aujourdhui: Jour
  envoi: boolean
  libelle: string
  onAnnuler: () => void
  /** `motif` : pourquoi le responsable change (vide sinon). */
  onValider: (s: SaisieAction, motif: string) => void
}): JSX.Element {
  const [s, setS] = useState<SaisieAction>(depart)
  const [motif, setMotif] = useState('')
  const idForm = useId()
  const changeDeMains = !!responsableInitial?.trim() && !!s.responsable.trim() && !memePersonne(s.responsable, responsableInitial)
  const complet = s.description.trim() && s.responsable.trim() && s.echeance && (!changeDeMains || motif.trim())
  // Mise à jour de document par le BE : 10 jours ouvrés au plus (28/10) — un rappel, pas un blocage.
  const avertissementBE = avertissementDelaiBE(s, s.echeance, ouverteLe || aujourdhui)
  return (
    <div className="flex flex-col gap-3" style={{ backgroundColor: 'var(--color-white)', border: '1px solid var(--color-black)', borderRadius: 'var(--radius-lg)', padding: 16 }}>
      <span style={{ fontSize: 14, fontWeight: 600 }}>{titre}</span>
      <div className="flex flex-col gap-1">
        <span style={STYLE_ETIQUETTE}>Catégorie</span>
        <div role="radiogroup" aria-label="Catégorie" className="grid" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
          {CATEGORIES_ACTION.map((c, k) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={s.categorie === c}
              title={LIBELLES_CATEGORIE_ACTION[c].fr}
              onClick={() => setS({ ...s, categorie: c })}
              style={{
                minHeight: 40,
                border: 'none',
                borderLeft: k > 0 ? '1px solid var(--color-border)' : 'none',
                backgroundColor: s.categorie === c ? 'var(--color-black)' : 'var(--color-white)',
                color: s.categorie === c ? 'var(--color-white)' : 'var(--color-black)',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {c}
            </button>
          ))}
        </div>
        <span style={STYLE_AIDE}>
          {LIBELLES_CATEGORIE_ACTION[s.categorie].fr} : {AIDES_CATEGORIE_ACTION[s.categorie].toLowerCase()}
        </span>
      </div>
      <label className="flex flex-col">
        <span style={STYLE_ETIQUETTE}>Action, en anglais</span>
        <textarea rows={2} value={s.description} onChange={(e) => setS({ ...s, description: e.target.value })} style={{ ...STYLE_CHAMP, resize: 'vertical' }} />
      </label>
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        <div className="flex flex-col" style={{ minWidth: 0 }}>
          <label htmlFor={`${idForm}-responsable`} style={STYLE_ETIQUETTE}>
            Responsable
          </label>
          <ChoixPersonne
            id={`${idForm}-responsable`}
            libelle="Responsable"
            valeur={{ nom: s.responsable, upn: s.responsableUPN }}
            habitues={habitues}
            titreHabitues="Souvent responsables d’actions"
            onChoisir={(p) =>
              setS((x) =>
                !p
                  ? { ...x, responsable: '', responsableUPN: '' }
                  : p.retrouvee
                    ? { ...x, responsableUPN: p.upn }
                    : { ...x, responsable: p.nom, responsableUPN: p.upn, service: p.service || serviceDe(p.nom) || x.service },
              )
            }
          />
        </div>
        <label className="flex flex-col">
          <span style={STYLE_ETIQUETTE}>{echeanceInitiale ? 'Échéance reportée' : 'Échéance'}</span>
          <ChoixDate
            libelle={echeanceInitiale ? 'Échéance reportée' : 'Échéance'}
            valeur={s.echeance}
            min={echeanceInitiale ? ajouterJours(echeanceInitiale, 1) : null}
            onChange={(j) => setS({ ...s, echeance: j })}
          />
          {echeanceInitiale && <span style={STYLE_AIDE}>Échéance initiale : {jourLisible(echeanceInitiale)} (gardée)</span>}
        </label>
      </div>
      <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
        <span style={STYLE_AIDE}>Dans :</span>
        {RACCOURCIS.map((r) => {
          const j = ajouterJours(aujourdhui, r.jours)
          return (
            <button
              key={r.libelle}
              type="button"
              onClick={() => setS({ ...s, echeance: j })}
              style={{
                border: `1px solid ${s.echeance === j ? 'var(--color-black)' : 'var(--color-border)'}`,
                borderRadius: 12,
                background: 'var(--color-white)',
                padding: '4px 10px',
                fontSize: 12,
                fontWeight: s.echeance === j ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              {r.libelle}
            </button>
          )
        })}
      </div>
      {avertissementBE && (
        <p className="flex items-start gap-2" style={{ margin: 0, fontSize: 13, lineHeight: '18px', color: 'var(--color-gray-90)' }}>
          <AlertCircle size={15} aria-hidden="true" style={{ flex: 'none', marginTop: 1 }} />
          {avertissementBE}
        </p>
      )}
      {changeDeMains && (
        <label className="flex flex-col">
          <span style={STYLE_ETIQUETTE}>
            Pourquoi la confier à {s.responsable.trim()} plutôt qu’à {responsableInitial?.trim()} ? (obligatoire, en anglais)
          </span>
          <input value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="ex. Away until December, Mathieu takes over" style={STYLE_CHAMP} />
        </label>
      )}
      <Boutons libelle={libelle} envoi={envoi} onAnnuler={onAnnuler} onValider={() => onValider(s, changeDeMains ? motif : '')} desactive={!complet} />
    </div>
  )
}
